import { useCallback, useEffect, useRef, useState } from 'react';
import { subscribeTranscriptUpdates, type TranscriptUpdate } from '../../utils/AmplifyTranscription/api';

export type TranscriptLine = {
  seq: number;
  text: string;
  isPartial: boolean;
  atTicks?: number;
  languageCode?: string;
  streamSid?: string;
};

type UseLiveTranscriptionArgs = {
  callSid?: string | null;
  enabled?: boolean;
};

export function useLiveTranscription({ callSid, enabled = true }: UseLiveTranscriptionArgs) {
  const [history, setHistory] = useState<TranscriptLine[]>([]);
  const [partial, setPartial] = useState<TranscriptLine | null>(null);
  const [error, setError] = useState<string | null>(null);
  const finalMapRef = useRef<Map<number, TranscriptLine>>(new Map());

  const reset = useCallback(() => {
    finalMapRef.current.clear();
    setHistory([]);
    setPartial(null);
    setError(null);
  }, []);

  useEffect(() => {
    reset();
  }, [callSid, reset]);

  useEffect(() => {
    if (!enabled || !callSid) return;
    console.info('[Transcription] hook enabled', { callSid });

    const unsubscribe = subscribeTranscriptUpdates(
      callSid,
      (evt) => {
        const update: TranscriptUpdate | undefined = evt?.data?.onTranscriptUpdate;
        if (!update) {
          console.debug('[Transcription] event without payload', evt);
          return;
        }
        console.debug('[Transcription] update', update);

        const seq = update.seq ?? -1;
        const line: TranscriptLine = {
          seq,
          text: update.text ?? '',
          isPartial: !!update.isPartial,
          atTicks: update.atTicks ?? undefined,
          languageCode: update.languageCode ?? undefined,
          streamSid: update.streamSid ?? undefined,
        };

        if (line.isPartial) {
          setPartial(line);
          return;
        }

        if (seq >= 0) {
          finalMapRef.current.set(seq, { ...line, isPartial: false });
          const sorted = Array.from(finalMapRef.current.values()).sort((a, b) => a.seq - b.seq);
          setHistory(sorted);
          setPartial((p) => (p && p.seq === seq ? null : p));
        }
      },
      (err) => {
        const message =
          (err as any)?.errors?.[0]?.message ??
          (err as any)?.message ??
          'Subscription error';
        console.error('[Transcription] hook error', message, err);
        setError(message);
      }
    );

    return () => {
      console.info('[Transcription] hook cleanup', { callSid });
      unsubscribe();
    };
  }, [callSid, enabled]);

  return { history, partial, error, reset };
}
